import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Alert } from '@abyss/web/ui/Alert';
import { V2Card } from '@abyss/web/ui/Card/v2';
import { Carousel, Slide } from '@abyss/web/ui/Carousel';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { IconBrand } from '@abyss/web/ui/IconBrand';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../../common/ConstantsStyles';
import { makeAllSlidesAriaHidden } from '../../../../../common/Utils/a11yUtils/a11yUtils';
import { useLagoon } from '../../../../../hooks/useLagoon';
import {
  getAllProvidersWithServiceType,
  setVirtualCareListViewPageParam,
} from '../utils';
import { VirtualCareProviderCard } from './VirtualCareProviderCard';
import {
  alertContainerStyle,
  carouselStyle,
  slideContainerStyle,
  slideStyle,
  viewAllCardCss,
  viewAllLinkCss,
} from './VirtualCareProvidersBanner.styled';
import { VirtualCareProviderModal } from './VirtualCareProvidersModal';

type VirtualCareProvidersBannerProps = {
  virtualCareProviders: any;
  setVirtualCareListView: (a: boolean) => void;
  expandedProviderListView: boolean;
};

export const VirtualCareProvidersBanner = ({
  expandedProviderListView = false,
  virtualCareProviders,
  setVirtualCareListView,
}: VirtualCareProvidersBannerProps) => {
  const [visibleAlerts, setVisibleAlerts] = useState([true, true]);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProviderName, setSelectedProviderName] = useState<
    string | null
  >(null);
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(mobileOnly);
  const data = useLagoon('ui-messaging')();
  const singleProviderContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_CONTENT_FOR_ONE,
  });
  const bannerContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_CONTENT,
  });
  const linkContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_LINK,
  });
  const allProviders = getAllProvidersWithServiceType(virtualCareProviders);

  const virtualCareProviderCount = allProviders?.length;

  const slides = [
    ...allProviders?.slice(0, 3).map((provider) => (
      <Slide css={slideStyle} key="slide">
        <Slide.Container css={slideContainerStyle} key="slide">
          <VirtualCareProviderCard
            key={provider?.providerName}
            mapView={!expandedProviderListView}
            mobileScreen={mobileScreen}
            provider={provider}
            serviceType={
              virtualCareProviders?.serviceType || provider?.serviceType
            }
            setIsModalOpen={setIsModalOpen}
            setSelectedProviderName={setSelectedProviderName}
          />
        </Slide.Container>
      </Slide>
    )),
  ];

  if (virtualCareProviderCount > 3) {
    slides.push(
      <V2Card
        css={viewAllCardCss(mobileScreen || !expandedProviderListView)}
        key="all-virtual-care-link"
        onClick={(e) => {
          e.preventDefault();
          // Update the URL with the new parameter
          const url = new URL(window.location.href);
          setVirtualCareListViewPageParam(url);
          setVirtualCareListView(true);
        }}
      >
        <Link
          after={<IconSymbol icon="chevron_right" size="20" />}
          css={viewAllLinkCss}
          data-auto-testid={`view-all-virtual-care-providers-link-text`}
          data-testid={`view-all-virtual-care-providers-link-text`}
        >
          {t('VIRTUAL_CARE_PROVIDERS_BANNER.ALL_VIRTUAL_CARE')}
        </Link>
      </V2Card>
    );
  }
  return (
    <React.Fragment>
      <Alert
        css={alertContainerStyle(mobileScreen || !expandedProviderListView)}
        data-testid="virtual-care-provider-banner"
        icon={
          <IconBrand
            css={{
              marginRight: '4px',
            }}
            icon="virtual_care"
            size={50}
            variant="twotone"
          />
        }
        isVisible={visibleAlerts[0]}
        onClose={() => {
          setVisibleAlerts([false, visibleAlerts[1]]);
        }}
        title={
          <Flex
            alignItems="center"
            css={{
              gap: mobileScreen
                ? '4px'
                : !expandedProviderListView
                ? '8px'
                : '16px',
              lineHeight: '22px',
              columnGap: '10px',
            }}
          >
            <Text
              data-testid="virtual-care-provider-banner-title"
              fontWeight={700}
              size="$lg"
            >
              {bannerContent?.title}
            </Text>
            {!expandedProviderListView && <br />}
            <Text
              data-testid="virtual-care-provider-banner-description"
              fontWeight={500}
              size="$sm"
            >
              {virtualCareProviderCount > 1
                ? bannerContent?.message?.replace(
                    '${count}',
                    virtualCareProviderCount
                  )
                : singleProviderContent?.message?.replace(
                    '${count}',
                    virtualCareProviderCount
                  )}
            </Text>
          </Flex>
        }
        variant="info"
      >
        <Flex
          alignItems="center"
          css={{
            gap: '4px',
            lineHeight: '20px',
            marginTop: '4px',
            marginBottom: '4px',
          }}
        >
          <Link
            css={{
              'abyss-link-root': {
                fontSize: '16px',
                lineHeight: '20px',
                marginBottom: '8px',
              },
            }}
            data-auto-testid="virtual-care-provider-link-text"
            data-testid="virtual-care-provider-link-text"
            onClick={(e) => {
              e.preventDefault();
              setIsOpen((prev) => !prev);
            }}
          >
            {linkContent?.title}
            <IconMaterial
              css={{ marginLeft: '4px' }}
              icon={isOpen ? 'expand_less' : 'expand_more'}
              size="20"
            />
          </Link>
        </Flex>
        <Flex css={{ width: '100%' }} direction="column">
          {isOpen &&
            (mobileScreen ||
            (!expandedProviderListView && virtualCareProviderCount > 2) ? (
              <Carousel
                css={carouselStyle(true)}
                data-auto-testid="virtual-care-provider-carousel"
                data-testid="virtual-care-provider-carousel"
                minimal
                nextSlideOnClick={() => makeAllSlidesAriaHidden()}
                noLoop
                prevSlideOnClick={() => makeAllSlidesAriaHidden()}
                slideIndexOnClick={() => makeAllSlidesAriaHidden()}
                slides={slides}
                slidesPerView={1}
              />
            ) : (
              <Flex
                css={{
                  width: '100%',
                  gap: '16px',
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                }}
                direction="row"
              >
                {slides?.map((slide, index) => (
                  <Flex
                    css={{
                      flex: '0 0 auto',
                      marginBottom: '8px',
                    }}
                    key={`slide-${index}`}
                  >
                    {slide}
                  </Flex>
                ))}
              </Flex>
            ))}
          <VirtualCareProviderModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            selectedProviderName={selectedProviderName}
            virtualCareProviders={virtualCareProviders}
          />
        </Flex>
      </Alert>
      {expandedProviderListView && <Divider color="$gray2" />}
    </React.Fragment>
  );
};
